const SESSION_KEY = "emera_attribution";
const FBC_STORAGE_KEY = "emera_fbc";

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

const CLICK_IDS = ["gclid", "fbclid", "ttclid", "ref"] as const;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  ref?: string;
  referrer?: string;
  landing_page?: string;
}

/**
 * Capture UTM params + click IDs from the current URL and persist in sessionStorage.
 * Should be called once on app load. Only stores on first visit (doesn't overwrite).
 */
export function initUtmTracker(): void {
  try {
    const params = new URLSearchParams(window.location.search);

    // Capture fbclid → persist as Meta-formatted _fbc fallback (long-lived).
    // Format spec: fb.{subdomain_index}.{creation_unix_ms}.{fbclid}
    // We use subdomain_index=1 (covers most production domains).
    const fbclid = params.get("fbclid")?.trim();
    if (fbclid && !localStorage.getItem(FBC_STORAGE_KEY)) {
      try {
        const fbc = `fb.1.${Date.now()}.${fbclid}`;
        localStorage.setItem(FBC_STORAGE_KEY, fbc);
      } catch { /* localStorage unavailable */ }
    }

    if (sessionStorage.getItem(SESSION_KEY)) return;

    const data: Attribution = {};

    for (const key of UTM_PARAMS) {
      const v = params.get(key)?.trim();
      if (v) data[key] = v.slice(0, 200);
    }

    for (const key of CLICK_IDS) {
      const v = params.get(key)?.trim();
      if (v) data[key] = v.slice(0, 500);
    }

    if (document.referrer) {
      data.referrer = document.referrer.slice(0, 500);
    }

    data.landing_page = (window.location.pathname + window.location.search).slice(0, 500);

    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    /* sessionStorage unavailable */
  }
}

/** Read a cookie by name from document.cookie, or undefined. */
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Returns the Meta `_fbp` browser cookie if present.
 * Format: fb.1.{ts}.{random}. Set automatically by the Meta Pixel.
 */
export function getFbp(): string | undefined {
  return readCookie("_fbp");
}

/**
 * Returns the Meta `_fbc` value. Prefers the official `_fbc` cookie set by
 * the Pixel; falls back to the value we synthesized from the URL `fbclid`
 * on first landing. Improves Meta EMQ when the Pixel cookie is missing.
 */
export function getFbc(): string | undefined {
  const cookie = readCookie("_fbc");
  if (cookie) return cookie;
  try {
    return localStorage.getItem(FBC_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Return stored attribution data, or an empty object if none exists.
 */
export function getAttribution(): Attribution {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Attribution;
  } catch {
    return {};
  }
}
